import styles from './RiskRegistryList.module.scss';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useTableComponents } from '../../components/tables/MTableComponents';
import { NetworkStatus } from '@apollo/client';
import MTTable from '../../components/tables/MTable';
// import { Button } from '../../components';
import RiskRegistryImportModal from './RiskRegistryImportModal';
import { useURLParams } from '../../providers/URLParamProvider';
import { registryStatus } from '../../utilities/risk_registry';
import { Text } from '../../components/typography';
import Label from '../../components/action_items/Label';
import { calculateRisk } from '../../utilities/riskMatrix';
import { useNavigate } from 'react-router-dom';
import {
  CustomDateRangeFilter,
  AutocompleteFilter,
} from '../../components/tables/MTableComponents';
import { dateTypes, getDateRange } from '../../components/tables/table';
import { useQueryParams } from '../../hooks/misc';
import { keys } from '../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';
import { paths } from '../../constants/strings';
import { useCurrentUser } from '../../providers/UserProvider';

const riskRegistryQuery = loader('./RiskRegistryList.fetch.graphql');

export default function RiskRegistryList() {
  const { textCell, userCell, dateCell, labelCell, userFilterOptions } =
    useTableComponents();
  const [show, setShow] = useState(false);
  const { filters, page, pageSize, sort, cachedDateFilters } = useURLParams();
  const navigate = useNavigate();
  const { getParam } = useQueryParams();
  const { t } = useTranslation();
  const { company } = useCurrentUser();
  const { riskMatrix } = company;
  const [dateSelectors, setDateSelectors] = useState(() => {
    return dateTypes.reduce((acc, [field, minKey, maxKey]) => {
      const [min, max] = getDateRange(
        cachedDateFilters?.[field],
        getParam(field),
      );
      acc[minKey] = min;
      acc[maxKey] = max;
      return acc;
    }, {});
  });
  const {
    networkStatus,
    previousData: { riskRegistryCount: oldCount } = {},
    refetch: refetchRegistry,
    data: { riskRegistry = [], riskRegistryCount = 0 } = {},
  } = useQuery(riskRegistryQuery, {
    variables: {
      options: {
        page,
        pageSize,
        sort,
        filters,
      },
    },
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'externalId',
        header: 'ID',
        Cell: ({ cell }) => textCell({ cell }),
      },
      {
        accessorKey: 'status',
        header: '',
        size: 100,
        Cell: ({ cell }) =>
          labelCell({
            name: registryStatus[cell.getValue()]?.title,
            color: registryStatus[cell.getValue()]?.color,
          }),
        filterVariant: 'select',
        filterSelectOptions: Object.entries(registryStatus).map(
          ([key, value]) => ({
            label: value.title,
            value: key,
          }),
        ),
      },
      {
        accessorKey: 'category',
        header: 'Hazard',
        Cell: ({ cell }) => textCell({ cell }),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        Cell: ({ cell }) => textCell({ cell }),
      },
      {
        accessorKey: 'riskScore',
        header: 'Initial Score',
        size: 100,
        Cell: ({ cell }) => {
          const risk = calculateRisk(cell.getValue());
          return <Label name={risk?.level} color={risk?.textColor} />;
        },
        filterVariant: 'select',
        filterSelectOptions: Object.entries(riskMatrix?.levels ?? {})?.map(
          ([key, value]) => ({
            label: key,
            value: `${value?.min},${value?.max}`,
          }),
        ),
      },
      {
        accessorKey: 'residualRiskScore',
        header: 'Mitigated Score',
        size: 100,
        Cell: ({ cell }) => {
          const risk = calculateRisk(cell.getValue()) || 0;
          return !risk ? (
            <Text noMargin size="sm" color="secondary">
              {t(keys.common.NONE)}
            </Text>
          ) : (
            <Label name={risk?.level} color={risk?.textColor} />
          );
        },
        filterVariant: 'select',
        filterSelectOptions: Object.entries(riskMatrix?.levels ?? {})?.map(
          ([key, value]) => ({
            label: key,
            value: `${value?.min},${value?.max}`,
          }),
        ),
      },
      {
        accessorFn: () => 'records',
        header: 'Records Count',
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => {
          const riskCount = row.original.risks?.length || 0;
          const incidentsCount = row.original.incidents?.length || 0;
          const cpaCount = row.original.correctiveActions?.length || 0;
          return (
            <Text noMargin weight="semiBold" size="sm">
              {riskCount + incidentsCount + cpaCount}
            </Text>
          );
        },
      },
      {
        accessorKey: 'nextReviewDate',
        header: 'Next Review Date',
        size: 275,
        Cell: ({ cell }) => dateCell({ cell }),
        Filter: ({ column }) => (
          <CustomDateRangeFilter
            column={column}
            minDate={dateSelectors.nextReviewDateMin}
            maxDate={dateSelectors.nextReviewDateMax}
            setMinDate={(value) =>
              setDateSelectors({ ...dateSelectors, nextReviewDateMin: value })
            }
            setMaxDate={(value) =>
              setDateSelectors({ ...dateSelectors, nextReviewDateMax: value })
            }
          />
        ),
      },
      {
        accessorKey: 'lastReviewDate',
        header: 'Last Reviewed',
        size: 275,
        Cell: ({ cell }) => dateCell({ cell }, { dateOnly: true }),
        Filter: ({ column }) => (
          <CustomDateRangeFilter
            column={column}
            minDate={dateSelectors.lastReviewDateMin}
            maxDate={dateSelectors.lastReviewDateMax}
            setMinDate={(value) =>
              setDateSelectors({ ...dateSelectors, lastReviewDateMin: value })
            }
            setMaxDate={(value) =>
              setDateSelectors({ ...dateSelectors, lastReviewDateMax: value })
            }
          />
        ),
      },
      {
        accessorKey: 'dateModified',
        header: t(keys.common.LAST_UPDATED),
        size: 275,
        Cell: ({ cell }) => dateCell({ cell }),
        Filter: ({ column }) => (
          <CustomDateRangeFilter
            column={column}
            minDate={dateSelectors.dateModifiedMin}
            maxDate={dateSelectors.dateModifiedMax}
            setMinDate={(value) =>
              setDateSelectors({ ...dateSelectors, dateModifiedMin: value })
            }
            setMaxDate={(value) =>
              setDateSelectors({ ...dateSelectors, dateModifiedMax: value })
            }
          />
        ),
      },
      {
        accessorKey: 'assignee',
        header: 'Owner',
        Cell: ({ cell }) => userCell({ cell }),
        Filter: ({ column }) => (
          <AutocompleteFilter
            items={userFilterOptions('all')}
            column={column}
            filterBy={'Owner'}
            type="user"
          />
        ),
      },
      {
        accessorKey: 'creator',
        header: 'Creator',
        Cell: ({ cell }) => userCell({ cell }),
        Filter: ({ column }) => (
          <AutocompleteFilter
            items={userFilterOptions('all')}
            column={column}
            filterBy={'Creator'}
            type="user"
          />
        ),
      },
    ],
    [
      labelCell,
      textCell,
      t,
      dateCell,
      dateSelectors,
      userCell,
      userFilterOptions,
      riskMatrix,
    ],
  );
  return (
    <div className={styles.page}>
      <MTTable
        refetch={refetchRegistry}
        isLoading={networkStatus !== NetworkStatus.ready}
        data={riskRegistry || []}
        columns={columns}
        rowCount={riskRegistryCount || oldCount}
        onRowClick={(risk) => navigate(`/${paths.registry}/${risk.id}`)}
        // customToolbar={
        //   <Button
        //     value="Import"
        //     icon="download"
        //     size="sm"
        //     align={'right'}
        //     outlined
        //     onClick={() => setShow(true)}
        //   />
        // }
      />
      <RiskRegistryImportModal show={show} setShow={setShow} />
    </div>
  );
}
